'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { FormError } from '@/components/form-error';
import { Form } from '@/components/ui/form';
import { SelectOptionVM } from '@/lib/models/view-models/view-models';
import { cn } from '@/lib/utils';
import { InvoiceInformation, InvoicingType } from '@/prisma/types';
import { AddInvoiceInformationSchema } from '@/schemas';
import { Button } from '@/ui-lib/button';
import { FormController } from '@/ui-lib/form/form-controller';
import { renderRadioButtonGroupFactory, renderSelectBox, renderTextInput } from '@/ui-lib/form/form-input-renders';
import { Paragraph } from '@/ui-lib/paragraph';

import { useInvoiceInformationFormAnalytics } from './invoice-information-analytics';

interface InitialInvoiceValues {
    type?: InvoicingType;
    personalName?: string;
    companyName?: string;
    vatNumber?: string;
    street?: string;
    city?: string;
    postalCode?: string;
    countryCode?: string;
}

interface InvoiceInformationFormProps {
    className?: string;
    teamId: number;
    countries: SelectOptionVM[];
    invoiceInformationAdded?: (invoiceInformation: InvoiceInformation) => void;
    initialValues?: InitialInvoiceValues;
    isCheckoutForm?: boolean;
}

async function setVatNumber(vatNumber: string): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await fetch('/app-api/payment-methods/set-vat-number', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ vatNumber }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, error: errorData.error || 'Failed to validate VAT number' };
        }

        return { success: true };
    } catch {
        return { success: false, error: 'Failed to validate VAT number' };
    }
}

async function addInvoiceInformation(
    values: z.infer<typeof AddInvoiceInformationSchema>
): Promise<{ success: boolean; data?: InvoiceInformation; error?: string }> {
    try {
        const response = await fetch('/app-api/invoice-information', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(values),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, error: errorData.error || 'Failed to save invoice information' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch {
        return { success: false, error: 'Failed to save invoice information' };
    }
}

export interface InvoiceInformationFormRefType {
    reset: () => void;
    getValues: () => z.infer<typeof AddInvoiceInformationSchema>;
    submit: (data: z.infer<typeof AddInvoiceInformationSchema>) => Promise<boolean>;
}

const InvoiceInformationFormInner = (
    {
        className,
        teamId,
        countries,
        invoiceInformationAdded,
        initialValues,
        isCheckoutForm = false,
    }: InvoiceInformationFormProps,
    ref: React.Ref<InvoiceInformationFormRefType>
) => {
    const [error, setError] = useState<string | undefined>('');
    const [isPending, setPending] = useState(false);

    const countryOptions = useMemo(
        () =>
            countries.map((country) => ({
                value: country.key,
                label: country.displayName,
            })),
        [countries]
    );

    const form = useForm<z.infer<typeof AddInvoiceInformationSchema>>({
        resolver: zodResolver(AddInvoiceInformationSchema),
        defaultValues: {
            accountType: initialValues?.type || 'COMPANY',
            companyName: initialValues?.companyName || '',
            vatNumber: initialValues?.vatNumber || '',
            street: initialValues?.street || '',
            city: initialValues?.city || '',
            postalCode: initialValues?.postalCode || '',
            countryCode: initialValues?.countryCode || undefined,
        },
    });

    const invoiceAnalytics = useInvoiceInformationFormAnalytics({
        formType: isCheckoutForm ? 'checkout' : 'settings',
    });

    const watchedAccountType = useWatch({ control: form.control, name: 'accountType' });
    const watchedCompanyName = useWatch({ control: form.control, name: 'companyName' });
    const watchedVatNumber = useWatch({ control: form.control, name: 'vatNumber' });
    const watchedStreet = useWatch({ control: form.control, name: 'street' });
    const watchedCity = useWatch({ control: form.control, name: 'city' });
    const watchedPostalCode = useWatch({ control: form.control, name: 'postalCode' });
    const watchedCountryCode = useWatch({ control: form.control, name: 'countryCode' });

    useEffect(() => {
        if (watchedAccountType) invoiceAnalytics.trackFieldStarted('accountType');
    }, [watchedAccountType, invoiceAnalytics]);

    useEffect(() => {
        if (watchedCompanyName?.trim()) invoiceAnalytics.trackFieldStarted('companyName');
    }, [watchedCompanyName, invoiceAnalytics]);

    useEffect(() => {
        if (watchedVatNumber?.trim()) invoiceAnalytics.trackFieldStarted('vatNumber');
    }, [watchedVatNumber, invoiceAnalytics]);

    useEffect(() => {
        if (watchedStreet?.trim()) invoiceAnalytics.trackFieldStarted('street');
    }, [watchedStreet, invoiceAnalytics]);

    useEffect(() => {
        if (watchedCity?.trim()) invoiceAnalytics.trackFieldStarted('city');
    }, [watchedCity, invoiceAnalytics]);

    useEffect(() => {
        if (watchedPostalCode?.trim()) invoiceAnalytics.trackFieldStarted('postalCode');
    }, [watchedPostalCode, invoiceAnalytics]);

    useEffect(() => {
        if (watchedCountryCode) invoiceAnalytics.trackFieldStarted('countryCode');
    }, [watchedCountryCode, invoiceAnalytics]);

    const onSubmit = async (values: z.infer<typeof AddInvoiceInformationSchema>) => {
        invoiceAnalytics.trackFormSubmitted({
            accountType: values.accountType,
            isCheckoutForm,
            hasVatNumber: values.accountType === 'COMPANY' && values.vatNumber?.trim() === '',
            hasInitialValues: !!initialValues,
        });
        setPending(true);
        setError(undefined);
        form.clearErrors(); // Clear all previous field errors before validation

        try {
            // Always call the api to set vat number. Note: If not a company we ignore the value from the form and
            //  send an empty string.
            //
            // The backend handles removing vat number from the stripe customer when an empty value is sent here.
            const vatNr = values.accountType === InvoicingType.COMPANY ? values.vatNumber : '';

            const vatResult = await setVatNumber(vatNr ?? '');

            if (!vatResult.success) {
                // VAT validation failed - show error on the VAT field
                form.setError('vatNumber', {
                    type: 'manual',
                    message: vatResult.error || 'VAT validation failed',
                });
                return false;
            }

            // VAT is valid (or not provided), now save the invoice information
            const response = await addInvoiceInformation(values);

            if (!response.success) {
                setError(response.error);

                console.error('Failed to add invoice information:', response.error);
                return false;
            }

            invoiceInformationAdded?.(response.data!);
            return true;
        } catch (err) {
            console.error('Error submitting invoice information:', err);

            setError('An unexpected error occurred');
            return false;
        } finally {
            setPending(false);
        }
    };

    useImperativeHandle(ref, () => ({
        reset: form.reset,
        getValues: form.getValues,
        trigger: form.trigger,
        submit: async (values?: z.infer<typeof AddInvoiceInformationSchema>) => {
            // If values provided, validate them first
            if (values) {
                const isValid = await form.trigger();
                if (!isValid) {
                    return false;
                }
                return onSubmit(values);
            }
            // Otherwise use handleSubmit which validates automatically
            return new Promise<boolean>((resolve) => {
                form.handleSubmit(
                    async (data) => {
                        const result = await onSubmit(data);
                        resolve(result);
                    },
                    () => {
                        resolve(false); // Validation failed
                    }
                )();
            });
        },
    }));

    const accountTypeRenderer = useMemo(
        () =>
            renderRadioButtonGroupFactory(
                [
                    { value: 'PERSONAL', label: 'Personal' },
                    { value: 'COMPANY', label: 'Company' },
                ],
                false,
                '',
                {
                    bordered: true,
                    block: true,
                }
            ),
        []
    );

    const accountType = useWatch({ control: form.control, name: 'accountType' });

    const countryField = (
        <div
            className={cn(
                'flex flex-col w-full gap-2',
                accountType === InvoicingType.PERSONAL ? 'col-span-2' : 'col-span-1'
            )}
        >
            <FormController
                control={form.control}
                field="countryCode"
                renderInput={renderSelectBox(countryOptions)}
                label="Country"
                required
            />
        </div>
    );

    const fields =
        accountType === InvoicingType.PERSONAL ? (
            countryField
        ) : (
            <>
                <div key="company-name-field" className="flex flex-col w-full col-span-2 gap-2">
                    <FormController
                        control={form.control}
                        field="companyName"
                        renderInput={renderTextInput}
                        label="Company Name"
                        required
                    />
                </div>
                <div className="flex flex-col w-full col-span-1 gap-2">
                    <FormController
                        control={form.control}
                        field="street"
                        renderInput={renderTextInput}
                        label="Street"
                        required
                    />
                </div>
                <div className="flex flex-col w-full col-span-1 gap-2">
                    <FormController
                        control={form.control}
                        field="city"
                        renderInput={renderTextInput}
                        label="City"
                        required
                    />
                </div>
                {countryField}
                <div className="flex flex-col w-full col-span-1 gap-2">
                    <FormController
                        label="Zip / Postal Code"
                        control={form.control}
                        field="postalCode"
                        renderInput={renderTextInput}
                        required
                    />
                </div>
                <div className="flex flex-col w-full col-span-2 gap-2 mt-2">
                    <FormController
                        label="VAT Number"
                        control={form.control}
                        field="vatNumber"
                        renderInput={renderTextInput}
                        required={false}
                    />
                </div>
            </>
        );

    return (
        <Form analyticsTrackingName={isCheckoutForm ? 'SelfCheckoutForm' : 'AddInvoiceInformation'} {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={cn('gap-6 w-full grid grid-cols-2', className)}>
                <div className="flex flex-col w-full col-span-2 gap-2">
                    <FormController
                        control={form.control}
                        field="accountType"
                        renderInput={accountTypeRenderer}
                        label=""
                        labelClassName="hidden"
                        extraProps={{ className: 'w-full' }}
                    />
                </div>

                {fields}

                {!isCheckoutForm && (
                    <Paragraph className="col-span-2 w-full">
                        By providing your invoice details, you allow CM Navigator A/S to invoice you for future payments
                        in accordance with their terms.
                    </Paragraph>
                )}

                <FormError message={error} />

                <div className="col-span-2 flex flex-row items-center justify-center pt-8">
                    <Button
                        className={classNames('w-full', {
                            'max-w-[200px]': !isCheckoutForm,
                        })}
                        type="submit"
                        disabled={isPending}
                        variant="tertiary"
                        isLoading={isPending}
                        data-testid="save-invoice-info"
                    >
                        Continue
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export const InvoiceInformationForm = forwardRef(InvoiceInformationFormInner);
